import { zodResolver } from '@hookform/resolvers/zod';
import { SignUpRequestDTO, UserRole } from '@pya-mms/common';
import axios from 'axios';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import { setAuthUser, setToken } from '../../../store/auth';
import { Cookies, setCookie, useBreakpoints } from '../../../utils';
import { Container, LinkButton, Text } from '../../ui';
import { FlexContainer } from '../../ui/Container';
import Stepper from '../../ui/Stepper';
import CategoryStep from './steps/CategoryStep';
import PaymentStep from './steps/PaymentStep';
import PlanSelectionStep from './steps/PlanSelectionStep';
import TermsStep from './steps/TermsStep';
import UserDetailsStep from './steps/UserDetailsStep';
import {
  MembershipCategory,
  MembershipTypeOption,
  STEP_CATEGORY,
  STEP_DETAILS,
  STEP_PAYMENT,
  STEP_PLAN,
  STEP_TERMS,
  WIZARD_STEPS,
  WizardFormData,
  WizardFormDataBlank,
} from './types';

// API Functions
const signUp = async (request: SignUpRequestDTO) => {
  const response = await axios.post('/signup', request);
  return response;
};

const fetchMembershipTypes = async () => {
  const { data } = await axios.get('/membership-rate-types');
  return data as MembershipTypeOption[];
};

const fetchNonMembershipTypes = async () => {
  const { data } = await axios.get('/membership-rate-types-nonmember');
  return data as MembershipTypeOption[];
};

const fetchPartnerMembershipTypes = async () => {
  const { data } = await axios.get('/membership-rate-types-partner');
  return data as MembershipTypeOption[];
};

// Combined schema for the entire wizard form
const WizardFormSchema = z.object({
  category: z.enum(['member', 'partner', 'non-member', ''] as const).refine((val) => val !== '', {
    message: 'Please select a category',
  }),
  membershipType: z.string(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(6, 'Phone number must be at least 6 characters'),
  dateOfBirth: z
    .date({
      required_error: 'Date of birth is required',
      invalid_type_error: 'Please enter a valid date',
    })
    .min(new Date('1900-01-01'), 'Date is too far in the past')
    .max(new Date(), 'Date cannot be in the future')
    .optional(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  termsAndConditionsAccepted: z.boolean(),
  isActive: z.boolean().optional(),
  userRole: z.nativeEnum(UserRole).optional(),
});

const SignupWizard: FC = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isMobileScreen, isSmallScreen } = useBreakpoints();

  const [currentStep, setCurrentStep] = useState(STEP_CATEGORY);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch membership types
  const { data: membershipTypes, isLoading: isLoadingMember } = useQuery('membershipTypes', fetchMembershipTypes);
  const { data: nonMemberTypes } = useQuery('nonMemberTypes', fetchNonMembershipTypes);
  const { data: partnerTypes, isLoading: isLoadingPartner } = useQuery('partnerTypes', fetchPartnerMembershipTypes);

  const { mutateAsync: signUpMutation } = useMutation(signUp);

  // Form setup
  const methods = useForm<WizardFormData>({
    defaultValues: WizardFormDataBlank,
    mode: 'onChange',
    resolver: zodResolver(WizardFormSchema),
  });

  const { watch, trigger, getValues, setValue } = methods;
  const category = watch('category');

  // Determine if non-member flow (skips plan and payment steps)
  const isNonMember = category === 'non-member';
  const isPartner = category === 'partner';

  // Get steps with visibility based on category
  const visibleSteps = useMemo(() => {
    return WIZARD_STEPS.map((step) => ({
      ...step,
      hidden: isNonMember && (step.id === 'plan' || step.id === 'payment'),
    }));
  }, [isNonMember]);

  // Get next step index considering hidden steps
  const getNextStep = useCallback(
    (current: number): number => {
      let next = current + 1;
      while (next < WIZARD_STEPS.length && visibleSteps[next].hidden) {
        next++;
      }
      return next;
    },
    [visibleSteps]
  );

  // Get previous step index considering hidden steps
  const getPrevStep = useCallback(
    (current: number): number => {
      let prev = current - 1;
      while (prev >= 0 && visibleSteps[prev].hidden) {
        prev--;
      }
      return prev;
    },
    [visibleSteps]
  );

  // Validate current step
  const validateStep = async (): Promise<boolean> => {
    let fields: (keyof WizardFormData)[];

    switch (currentStep) {
      case STEP_CATEGORY:
        fields = ['category'];
        break;
      case STEP_PLAN:
        fields = ['membershipType'];
        break;
      case STEP_DETAILS:
        fields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'password'];
        break;
      case STEP_TERMS:
        fields = ['termsAndConditionsAccepted'];
        break;
      default:
        return true;
    }

    const result = await trigger(fields);
    return result;
  };

  // Handle next step
  const handleNext = async () => {
    const isValid = await validateStep();
    if (!isValid) return;

    // If on category step and selecting non-member, auto-set membershipType
    if (currentStep === STEP_CATEGORY && isNonMember && nonMemberTypes?.[0]) {
      setValue('membershipType', nonMemberTypes[0].id);
    }

    const nextStep = getNextStep(currentStep);
    if (nextStep < WIZARD_STEPS.length) {
      setCurrentStep(nextStep);
    }
  };

  // Handle previous step
  const handleBack = () => {
    const prevStep = getPrevStep(currentStep);
    if (prevStep >= 0) {
      setCurrentStep(prevStep);
    }
  };

  // Handle step click from stepper
  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const isValid = await validateStep();
    if (!isValid) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const formData = getValues();

      // Convert form data to API format
      const dateOfBirthString = formData.dateOfBirth ? dayjs.utc(formData.dateOfBirth).format('YYYY-MM-DD') : '';

      const values: SignUpRequestDTO = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: dateOfBirthString,
        membershipType: formData.membershipType,
        termsAndConditionsAccepted: formData.termsAndConditionsAccepted,
        password: formData.password,
        isActive: isNonMember ? true : undefined,
        userRole: isNonMember ? UserRole.NonMember : isPartner ? UserRole.Partner : undefined,
      };

      const { data } = await signUpMutation(values);
      const { user, token } = data;

      dispatch(setAuthUser({ ...user, isOnline: true }));
      dispatch(setToken(token));
      setCookie(Cookies.Token, token);
      axios.defaults.headers.common = { Authorization: `bearer ${token}` };

      if (isNonMember) {
        router.push('/');
      } else {
        // Move to payment step
        setCurrentStep(STEP_PAYMENT);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrorMessage(error?.response?.data || 'An error occurred during signup');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render current step content
  const renderStep = () => {
    switch (currentStep) {
      case STEP_CATEGORY:
        return <CategoryStep onNext={handleNext} />;
      case STEP_PLAN:
        return (
          <PlanSelectionStep
            category={category as MembershipCategory}
            membershipTypes={membershipTypes || []}
            partnerTypes={partnerTypes || []}
            isLoading={isLoadingMember || isLoadingPartner}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case STEP_DETAILS:
        return <UserDetailsStep onNext={handleNext} onBack={handleBack} />;
      case STEP_TERMS:
        return (
          <TermsStep
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            isNonMember={isNonMember}
          />
        );
      case STEP_PAYMENT:
        return <PaymentStep />;
      default:
        return null;
    }
  };

  const paddingHorizontal = isMobileScreen ? 'xxs' : isSmallScreen ? 'md' : 'xxl';

  return (
    <FormProvider {...methods}>
      <Container paddingHorizontal={paddingHorizontal} paddingVertical="xs">
        <FlexContainer flexDirection="column" gap="md">
          {/* Stepper */}
          <Stepper
            steps={visibleSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            allowBackNavigation={true}
          />

          {/* Error message */}
          {errorMessage && (
            <Text color="error" style={{ textAlign: 'center' }}>
              {errorMessage}
            </Text>
          )}

          {/* Step content */}
          {renderStep()}

          {/* Login link */}
          {currentStep !== STEP_PAYMENT && (
            <FlexContainer justify="center" gap="xs">
              <Text size="xs" color="black">
                Already have an account?
              </Text>
              <LinkButton size="xs" type="button" color="link" onClick={() => router.push('/login')}>
                Log in
              </LinkButton>
            </FlexContainer>
          )}
        </FlexContainer>
      </Container>
    </FormProvider>
  );
};

export default SignupWizard;
